/**
 * The comparison rules behind every sortable table.
 *
 * @remarks
 * Pure and React-free on purpose: this is the part with the interesting
 * behaviour — null grouping, signed numbers, composite keys, a total order —
 * and keeping it out of the component file means it can be exercised directly
 * against real API payloads. The hook and the header components that use it
 * live in `components/ui.tsx` and re-export everything here.
 */

import type { Streak } from '../types'

/** Which way a column is ordered. */
export type SortDirection = 'asc' | 'desc'

/**
 * One row's position on a column's axis.
 *
 * @remarks
 * `null`, `undefined`, `''` and `NaN` all mean "this row has no value here" —
 * an unassigned conference, a player with no games, an em-dash placeholder.
 * Missing values are never interleaved with real ones: they are pinned to the
 * end of the table in *both* directions, so flipping a column never buries the
 * rows you were reading under a block of blanks.
 */
export type SortValue = number | string | null | undefined

/**
 * What a column sorts on: one value, or a tuple compared left to right.
 *
 * @remarks
 * The tuple form exists for columns whose rendered text is a composite, such as
 * a `W-L` record. A tuple counts as missing only when it is empty or its first
 * element is missing, so a present-but-partial composite still sorts.
 */
export type SortKey = SortValue | readonly SortValue[]

function isTuple(value: SortKey): value is readonly SortValue[] {
  return Array.isArray(value)
}

/** Whether a key means "this row has nothing to compare". */
export function isMissing(value: SortKey): boolean {
  if (isTuple(value)) return value.length === 0 || isMissing(value[0])
  if (value === null || value === undefined || value === '') return true
  return typeof value === 'number' && Number.isNaN(value)
}

/**
 * Orders two present values.
 *
 * @remarks
 * Numbers compare numerically so a signed differential of `-100` lands below
 * `+5` rather than after it alphabetically. Anything else compares as text with
 * `numeric` collation, which keeps `Season 9` before `Season 10`.
 */
function compareScalar(a: SortValue, b: SortValue): number {
  if (typeof a === 'number' && typeof b === 'number') {
    return a === b ? 0 : a < b ? -1 : 1
  }
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

function compareKeys(a: SortKey, b: SortKey): number {
  if (isTuple(a) && isTuple(b)) {
    const length = Math.max(a.length, b.length)
    for (let index = 0; index < length; index += 1) {
      const step = compareScalar(a[index], b[index])
      if (step !== 0) return step
    }
    return 0
  }
  return compareScalar(a as SortValue, b as SortValue)
}

/**
 * Applies one column's ordering to a copy of the rows.
 *
 * @param rows - Source rows in their natural order.
 * @param accessor - The column's sort key for a row.
 *
 * @remarks
 * Position in the incoming array is the final tiebreak, and it is *not* negated
 * for a descending sort. Three properties follow, and the third is the reason
 * for the asymmetry:
 *
 * - The comparator is a total order — no two rows ever compare equal — so the
 *   result depends only on the input, never on engine sort stability or on what
 *   was on screen a moment ago.
 * - Flipping direction reverses the order of the distinct values.
 * - Rows sharing a value keep the server's order in *both* directions. On a
 *   season table where six players are 6-4, that order is the league's own
 *   tiebreakers; reversing it on every click would look like the table was
 *   scrambling rows it had just been asked to sort by.
 *
 * @example
 * ```typescript
 * sortRows(players, player => player.pointDifferential, 'desc')
 * ```
 */
export function sortRows<Row>(
  rows: readonly Row[],
  accessor: (row: Row) => SortKey,
  direction: SortDirection
): Row[] {
  const sign = direction === 'asc' ? 1 : -1
  const decorated = rows.map((row, index) => ({ row, index, key: accessor(row) }))

  decorated.sort((a, b) => {
    const aMissing = isMissing(a.key)
    const bMissing = isMissing(b.key)
    if (aMissing || bMissing) {
      if (aMissing !== bMissing) return aMissing ? 1 : -1
      return a.index - b.index
    }
    const step = compareKeys(a.key, b.key)
    return step !== 0 ? sign * step : a.index - b.index
  })

  return decorated.map(entry => entry.row)
}

/**
 * Picks the direction one click should apply, from the data itself.
 *
 * @returns Descending for a numeric column so the best row lands on top,
 * ascending for text so a name column reads A–Z.
 *
 * @remarks
 * Sampling the first row that actually has a value keeps a column of numbers
 * from opening ascending just because the top few players have not played yet.
 */
export function defaultDirection<Row>(
  rows: readonly Row[],
  accessor: (row: Row) => SortKey
): SortDirection {
  for (const row of rows) {
    const key = accessor(row)
    if (isMissing(key)) continue
    const sample = isTuple(key) ? key[0] : key
    return typeof sample === 'number' ? 'desc' : 'asc'
  }
  return 'asc'
}

/**
 * Signed length of a result run, for sorting a `W3` / `L5` column.
 *
 * @param streak - Null for a player with no official games.
 * @returns Wins positive, losses negative, so one axis runs from the longest
 * winning run down through `W1`, `L1`, and on to the longest losing run. A
 * player with no games returns null and sorts to the end in both directions
 * rather than pretending to sit in the middle of that scale.
 *
 * @remarks
 * Sorting the rendered text instead would order `L5` before `W1` and put `W10`
 * between `W1` and `W2`, which is why this exists.
 *
 * @example
 * ```typescript
 * streakSortValue({ result: 'win', length: 3 })  //  3
 * streakSortValue({ result: 'loss', length: 5 }) // -5
 * streakSortValue(null)                          // null
 * ```
 */
export function streakSortValue(streak: Streak | null): number | null {
  if (!streak) return null
  return streak.result === 'win' ? streak.length : -streak.length
}

/**
 * Sort key for a `W-L` record column.
 *
 * @returns Wins first, then losses inverted, so `8-2` outranks `8-3` and both
 * outrank `7-0`. Deliberately *not* win percentage: the column reads `8-2`, and
 * a visitor clicking it expects the most wins on top, not the best rate from a
 * player with two games.
 *
 * @example
 * ```typescript
 * recordSortKey(8, 2) // [8, -2]
 * ```
 */
export function recordSortKey(wins: number, losses: number): readonly number[] {
  return [wins, -losses]
}

/**
 * Sort key for a column rendering a timestamp.
 *
 * @param value - ISO timestamp, or null when the date was never recorded.
 * @returns Epoch milliseconds, or null so undated rows group at the end.
 */
export function dateSortValue(value: string | null | undefined): number | null {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}
